import { z } from "zod";
import { UNIT_CODES } from "../units";
import { pageQuery } from "./common";

/** Склад basic (ТЗ 08 §8.4–8.5): остатки, карточка движения, инвентаризация. */
const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });
const bool = z.enum(["true", "false"]).transform((v) => v === "true");

/** Все типы складских документов; вручную создаются только receipt / writeoff. */
export const STOCK_DOC_ALL_TYPES = ["receipt", "writeoff", "consumption", "inventory_adjustment"] as const;
export type StockDocAnyType = (typeof STOCK_DOC_ALL_TYPES)[number];

export const stockBalancesQuery = pageQuery.extend({
  /** Место хранения (addon.inventory_pro) */
  locationId: z.uuid().optional(),
  /** Склад филиала; по умолчанию — основной (несколько складов — addon.inventory_pro) */
  warehouseId: z.uuid().optional(),
  categoryId: z.uuid().optional(),
  /** Только ниже минимума или в минусе */
  low: bool.optional(),
  /** Показать продукты без движений (нулевой остаток) */
  all: bool.optional(),
});

/** Карточка движения продукта (FR-INV-003): период включительно. */
export const stockMovementsQuery = z
  .object({ productId: z.uuid(), from: isoDate, to: isoDate, warehouseId: z.uuid().optional() })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] });

export const INVENTORY_SCOPES = ["all", "category", "products", "location"] as const;
export const INVENTORY_COUNT_STATUSES = ["counting", "posted", "cancelled"] as const;

export const inventoryCountCreateSchema = z
  .strictObject({
    scope: z.enum(INVENTORY_SCOPES),
    /** Склад филиала; по умолчанию — основной */
    warehouseId: z.uuid().nullable().optional(),
    categoryId: z.uuid().nullable().optional(),
    productIds: z.array(z.uuid()).max(2000).optional(),
    /** «Слепая»: подсчитывающий не видит системный остаток (FR-CNT-001) */
    blind: z.boolean().default(false),
    /** scope = location: место хранения склада (addon.inventory_pro) */
    locationId: z.uuid().nullable().optional(),
    comment: z.string().trim().max(500, { error: "Максимум 500 символов" }).nullable().optional(),
  })
  .refine((v) => v.scope !== "category" || !!v.categoryId, { error: "Выберите категорию", path: ["categoryId"] })
  .refine((v) => v.scope !== "location" || !!v.locationId, { error: "Выберите место хранения", path: ["locationId"] })
  .refine((v) => v.scope !== "products" || (v.productIds?.length ?? 0) > 0, { error: "Выберите продукты", path: ["productIds"] });
export type InventoryCountCreateInput = z.input<typeof inventoryCountCreateSchema>;

/** Подсчёт строк: факт в любой совместимой единице; null — сбросить подсчёт строки. */
export const inventoryCountLinesSchema = z.strictObject({
  lines: z.array(z.strictObject({
    productId: z.uuid(),
    qty: z.number({ error: "Укажите количество" }).min(0, { error: "Не меньше нуля" }).max(1e9).nullable(),
    unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }).optional(),
  })).min(1).max(500),
});
export type InventoryCountLinesInput = z.input<typeof inventoryCountLinesSchema>;

export const inventoryCountsQuery = pageQuery.extend({ status: z.enum(INVENTORY_COUNT_STATUSES).optional() });

// ───────────── Склад (расширенный): склады, сроки годности, перемещения (addon.inventory_pro, ТЗ 08 §8.4) ─────────────

const qtyLine = z.strictObject({
  productId: z.uuid({ error: "Выберите продукт" }),
  qty: z.number({ error: "Укажите количество" }).positive({ error: "Количество должно быть больше нуля" }).max(1e9),
  unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }),
});
const comment = z.string().trim().max(500, { error: "Максимум 500 символов" }).nullable().optional();

/** Назначение склада: основной, кухня, бар, центральный (отпускает в филиалы), прочий. */
export const WAREHOUSE_KINDS = ["main", "kitchen", "bar", "central", "other"] as const;
export type WarehouseKind = (typeof WAREHOUSE_KINDS)[number];

export const warehouseSchema = z.strictObject({
  name: z.string().trim().min(1, { error: "Обязательное поле" }).max(120, { error: "Максимум 120 символов" }),
  kind: z.enum(WAREHOUSE_KINDS).default("main"),
  /** BR-111: списания и перемещения в минус разрешены */
  negativeAllowed: z.boolean().default(false),
  /** Основной склад филиала: с него списываются продажи по ТТК */
  isDefault: z.boolean().optional(),
});
export type WarehouseInput = z.input<typeof warehouseSchema>;
export const warehouseUpdateSchema = warehouseSchema.partial().extend({ version: z.number().int().min(1) });
export const warehousesQuery = z.object({
  /** Склады всех филиалов компании (выбор получателя перемещения) */
  all: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
});

/** Партии со сроками (FEFO, BR-113/114/115): expired — просроченные, soon — истекают за `days` дней. */
export const BATCH_STATES = ["expired", "soon", "ok", "none"] as const;
export type BatchState = (typeof BATCH_STATES)[number];
export const stockBatchesQuery = pageQuery.extend({
  warehouseId: z.uuid().optional(),
  productId: z.uuid().optional(),
  state: z.enum(["expired", "soon", "dated", "all"]).default("dated"),
  /** Порог «скоро истекает», дней (по умолчанию — из настроек склада, BR-115) */
  days: z.coerce.number().int().min(1).max(90).optional(),
});

export const STOCK_TRANSFER_STATUSES = ["in_transit", "received", "cancelled"] as const;
export type StockTransferStatus = (typeof STOCK_TRANSFER_STATUSES)[number];

/**
 * Перемещение (BR-119/120): со склада своего филиала на любой склад компании. Внутри филиала — сразу (одна фаза),
 * в другой филиал — «в пути» до приёмки получателем.
 */
export const stockTransferSchema = z
  .strictObject({
    fromWarehouseId: z.uuid({ error: "Выберите склад-отправитель" }),
    toWarehouseId: z.uuid({ error: "Выберите склад-получатель" }),
    comment,
    lines: z.array(qtyLine).min(1, { error: "Добавьте хотя бы одну строку" }).max(200),
  })
  .refine((t) => t.fromWarehouseId !== t.toWarehouseId, { error: "Склады должны различаться", path: ["toWarehouseId"] });
export type StockTransferInput = z.input<typeof stockTransferSchema>;

/** Приёмка: фактически принятое количество по строкам (не больше отправленного); недостача фиксируется в перемещении. */
export const stockTransferReceiveSchema = z.strictObject({
  lines: z.array(z.strictObject({
    productId: z.uuid(),
    qty: z.number({ error: "Укажите количество" }).min(0, { error: "Не меньше нуля" }).max(1e9),
    unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }).optional(),
  })).max(200).optional(),
  comment,
});
export type StockTransferReceiveInput = z.input<typeof stockTransferReceiveSchema>;

export const stockTransfersQuery = pageQuery.extend({
  status: z.enum(STOCK_TRANSFER_STATUSES).optional(),
  /** in — к нам, out — от нас */
  direction: z.enum(["in", "out"]).optional(),
});

// ───────────── Производство полуфабрикатов (addon.production, ТЗ 08 §8.8, BR-130) ─────────────

export const PRODUCTION_STATUSES = ["planned", "in_progress", "completed", "cancelled"] as const;
export type ProductionStatus = (typeof PRODUCTION_STATUSES)[number];

const prodQty = z.number({ error: "Укажите количество" }).positive({ error: "Количество должно быть больше нуля" }).max(1e9);
const prodUnit = z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" });

/** Производственный заказ: ПФ (продукт с ТТК), плановый выпуск, склады сырья и ПФ (по умолчанию — основной). */
export const productionOrderSchema = z.strictObject({
  productId: z.uuid({ error: "Выберите полуфабрикат" }),
  plannedQty: prodQty,
  unit: prodUnit,
  plannedDate: isoDate,
  rawWarehouseId: z.uuid().nullable().optional(),
  outputWarehouseId: z.uuid().nullable().optional(),
  comment,
});
export type ProductionOrderInput = z.input<typeof productionOrderSchema>;
export const productionOrderUpdateSchema = z.strictObject({
  plannedQty: prodQty.optional(),
  unit: prodUnit.optional(),
  plannedDate: isoDate.optional(),
  rawWarehouseId: z.uuid().nullable().optional(),
  outputWarehouseId: z.uuid().nullable().optional(),
  comment,
  version: z.number().int().min(1),
});

/**
 * Выпуск: фактический выход ПФ; фактический расход сырья по строкам (по умолчанию — по плану).
 * Себестоимость 1 ед. ПФ = стоимость списанного сырья ÷ фактический выход (AC-PRD-01).
 */
export const productionCompleteSchema = z.strictObject({
  actualQty: prodQty,
  unit: prodUnit,
  lines: z.array(z.strictObject({ productId: z.uuid(), qty: z.number().min(0, { error: "Не меньше нуля" }).max(1e9), unit: prodUnit })).max(200).optional(),
  /** Срок годности партии ПФ; по умолчанию — дата выпуска + срок хранения продукта */
  expiresAt: isoDate.nullable().optional(),
  comment,
});
export type ProductionCompleteInput = z.input<typeof productionCompleteSchema>;

export const productionOrdersQuery = pageQuery.extend({
  status: z.enum(PRODUCTION_STATUSES).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
/** Потребность в сырье до создания заказа (форма). */
export const productionRequirementsQuery = z.object({
  productId: z.uuid(),
  qty: z.coerce.number().positive().max(1e9),
  unit: prodUnit,
  warehouseId: z.uuid().optional(),
});

// ───────────── Склад (расширенный): настройки, места хранения, заявки, разделка и сборка ─────────────

/** Настройки склада компании (BR-114/115/133). */
export const inventorySettingsSchema = z.strictObject({
  /** BR-114: в продаже просроченные партии — только если свежих нет */
  blockExpired: z.boolean().optional(),
  /** BR-115: «скоро истекает» за N дней */
  expiryWarningDays: z.number().int().min(1, { error: "От 1 до 60" }).max(60, { error: "От 1 до 60" }).optional(),
  /** BR-133: ПФ нет на складе — списывать сырьём по его ТТК */
  explodeSemiWhenMissing: z.boolean().optional(),
  /** Запас к прогнозу плана производства, % */
  productionSafetyPct: z.number().int().min(0, { error: "От 0 до 100" }).max(100, { error: "От 0 до 100" }).optional(),
});
export type InventorySettingsInput = z.input<typeof inventorySettingsSchema>;

export const warehouseLocationSchema = z.strictObject({
  name: z.string().trim().min(1, { error: "Обязательное поле" }).max(80, { error: "Максимум 80 символов" }),
  sort: z.number().int().min(0).max(10_000).optional(),
});
/** Привязка продуктов к местам хранения склада; locationId null — убрать привязку. */
export const productLocationsSchema = z.strictObject({
  items: z.array(z.strictObject({ productId: z.uuid(), locationId: z.uuid().nullable() })).min(1).max(500),
});

/** Заявка на центральный склад (FR-INV-008): что нужно на склад своего филиала со склада другого филиала. */
export const STOCK_REQUEST_STATUSES = ["submitted", "shipped", "received", "rejected", "cancelled"] as const;
export type StockRequestStatus = (typeof STOCK_REQUEST_STATUSES)[number];
export const stockRequestSchema = z
  .strictObject({
    warehouseId: z.uuid().nullable().optional(),
    supplierWarehouseId: z.uuid({ error: "Выберите склад, у которого заказываете" }),
    neededBy: isoDate.nullable().optional(),
    comment,
    lines: z.array(qtyLine).min(1, { error: "Добавьте хотя бы одну строку" }).max(200),
  });
export type StockRequestInput = z.input<typeof stockRequestSchema>;
/** Отгрузка по заявке: количество по продуктам (без строк — как заказано); создаётся перемещение «в пути». */
export const stockRequestShipSchema = z.strictObject({
  lines: z.array(z.strictObject({
    productId: z.uuid(),
    qty: z.number().min(0, { error: "Не меньше нуля" }).max(1e9),
    unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }).optional(),
  })).max(200).optional(),
  comment,
});
export const stockRequestRejectSchema = z.strictObject({ reason: z.string().trim().min(1, { error: "Укажите причину" }).max(500, { error: "Максимум 500 символов" }) });
export const stockRequestsQuery = pageQuery.extend({
  status: z.enum(STOCK_REQUEST_STATUSES).optional(),
  /** out — наши заявки, in — заявки к нам (мы — центральный склад) */
  direction: z.enum(["in", "out"]).optional(),
});

/** Разделка (1 → N) и сборка (N → 1), BR-131: себестоимость входа делится между выходами по долям. */
export const CONVERSION_TYPES = ["disassembly", "assembly"] as const;
export type ConversionType = (typeof CONVERSION_TYPES)[number];
const convOut = qtyLine.extend({
  /** Доля себестоимости, %; не заданы — пропорционально цене закупки × количество (или количеству) */
  share: z.number().min(0, { error: "Не меньше 0" }).max(100, { error: "Не больше 100" }).nullable().optional(),
});
export const conversionSchema = z
  .strictObject({
    type: z.enum(CONVERSION_TYPES),
    date: isoDate,
    warehouseId: z.uuid().nullable().optional(),
    comment,
    inputs: z.array(qtyLine).min(1, { error: "Добавьте, что разделываете" }).max(50),
    outputs: z.array(convOut).min(1, { error: "Добавьте, что получилось" }).max(50),
  })
  .refine((c) => c.type !== "disassembly" || c.inputs.length === 1, { error: "При разделке — один продукт на входе", path: ["inputs"] })
  .refine((c) => c.type !== "assembly" || c.outputs.length === 1, { error: "При сборке — один продукт на выходе", path: ["outputs"] })
  .refine((c) => !c.outputs.some((o) => o.share != null) || c.outputs.every((o) => o.share != null), { error: "Укажите доли у всех выходов или ни у одного", path: ["outputs"] })
  .refine((c) => !c.outputs.every((o) => o.share != null) || Math.abs(c.outputs.reduce((s, o) => s + (o.share ?? 0), 0) - 100) < 0.01, { error: "Сумма долей должна быть 100%", path: ["outputs"] })
  .refine((c) => !c.inputs.some((i) => c.outputs.some((o) => o.productId === i.productId)), { error: "Продукт не может быть и входом, и выходом", path: ["outputs"] });
export type ConversionInput = z.input<typeof conversionSchema>;
export const conversionTemplateSchema = z.strictObject({
  type: z.enum(CONVERSION_TYPES),
  name: z.string().trim().min(1, { error: "Обязательное поле" }).max(120, { error: "Максимум 120 символов" }),
  inputs: z.array(qtyLine).min(1).max(50),
  outputs: z.array(convOut).min(1).max(50),
});

/** Предложение плана производства на дату (прогноз по тому же дню недели за 4 недели). */
export const productionSuggestionsQuery = z.object({ date: isoDate, weeks: z.coerce.number().int().min(1).max(8).default(4) });
